import { useId, useState } from "react";
import type { ResourceMonthTotal } from "@/hooks/use-hours-reports";
import { cn } from "@/lib/utils";
import { BASIS_LABEL, formatMonthColumn, hrs, pct } from "./report-format";

/**
 * A Resource's 13 months in hours (spec S3.2.1), one axis:
 * - Guidance (sky) and Actuals (violet) as thin paired bars — Actuals only up to the
 *   current month, which shows hours logged so far and a "Guidance to date" pace tick;
 * - Capacity as a neutral step line across every month;
 * - forecast months hatched, so a planned figure never reads as a logged one.
 * Validated pair: chart-2 / chart-1 pass the CVD and normal-vision checks; both sit below
 * 3:1 on white, so every value is also in the hover tooltip and the table below.
 */
const COL = 56;
const H = 200;
const PAD_TOP = 12;
const PAD_BOTTOM = 26;
const BAR = 14;
const GAP = 2;

export function ResourceHoursChart({
  totals,
  selectedMonth,
  onSelectMonth,
  compact = false,
}: {
  totals: ResourceMonthTotal[];
  selectedMonth?: string | null;
  onSelectMonth?: (month: string) => void;
  compact?: boolean;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const hatchId = `forecast-hatch-${useId()}`;
  const width = totals.length * COL;
  const height = compact ? 150 : H;
  const plotH = height - PAD_TOP - PAD_BOTTOM;
  const max = Math.max(
    1,
    ...totals.flatMap((t) => [t.capacityHours, t.guidanceHours, t.actualHours ?? 0]),
  );
  const y = (v: number) => PAD_TOP + plotH - (v / max) * plotH;
  const barH = (v: number) => Math.max(0, (v / max) * plotH);

  // Capacity as a step line: flat across each month's column.
  const capacityPath = totals
    .map(
      (t, i) => `${i === 0 ? "M" : "L"}${i * COL + 4},${y(t.capacityHours)} H${(i + 1) * COL - 4}`,
    )
    .join(" ");

  const hovered = hover === null ? null : (totals[hover] ?? null);

  return (
    <figure className="flex flex-col gap-2">
      <figcaption className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-chart-2" aria-hidden /> Guidance
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-chart-1" aria-hidden /> Actuals
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0.5 w-4 bg-foreground/70" aria-hidden /> Capacity
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="report-forecast h-2.5 w-4 rounded-sm border border-border" aria-hidden />
          Forecast
        </span>
      </figcaption>
      <div className="relative">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="h-auto w-full"
          role="img"
          aria-label="Monthly Guidance, Actuals and Capacity in hours"
        >
          <defs>
            <pattern
              id={hatchId}
              width={6}
              height={6}
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <line
                x1={0}
                y1={0}
                x2={0}
                y2={6}
                stroke="var(--muted-foreground)"
                strokeOpacity={0.18}
                strokeWidth={1}
              />
            </pattern>
          </defs>
          {totals.map((t, i) => {
            const x0 = i * COL;
            const center = x0 + COL / 2;
            const guidanceX = t.basis === "forecast" ? center - BAR / 2 : center - BAR - GAP / 2;
            const actualX = center + GAP / 2;
            const selected = selectedMonth === t.month;
            return (
              <g key={t.month}>
                {t.basis === "forecast" && (
                  <rect x={x0} y={PAD_TOP} width={COL} height={plotH} fill={`url(#${hatchId})`} />
                )}
                {(selected || hover === i) && (
                  <rect
                    x={x0 + 1}
                    y={PAD_TOP}
                    width={COL - 2}
                    height={plotH}
                    rx={4}
                    fill="var(--muted)"
                    opacity={selected ? 1 : 0.6}
                  />
                )}
                <rect
                  x={guidanceX}
                  y={y(t.guidanceHours)}
                  width={BAR}
                  height={barH(t.guidanceHours)}
                  rx={3}
                  fill="var(--chart-2)"
                />
                {t.actualHours !== null && (
                  <rect
                    x={actualX}
                    y={y(t.actualHours)}
                    width={BAR}
                    height={barH(t.actualHours)}
                    rx={3}
                    fill="var(--chart-1)"
                  />
                )}
                {t.guidanceToDateHours !== null && (
                  <line
                    x1={actualX - 3}
                    x2={actualX + BAR + 3}
                    y1={y(t.guidanceToDateHours)}
                    y2={y(t.guidanceToDateHours)}
                    stroke="var(--foreground)"
                    strokeWidth={2}
                    strokeDasharray="3 2"
                  />
                )}
                <text
                  x={center}
                  y={height - 8}
                  textAnchor="middle"
                  fill={t.basis === "inProgress" ? "var(--foreground)" : "var(--muted-foreground)"}
                  style={{ fontSize: "var(--fs-chart-label)" }}
                >
                  {formatMonthColumn(t.month, i === 0)}
                </text>
              </g>
            );
          })}
          <line
            x1={0}
            x2={width}
            y1={PAD_TOP + plotH}
            y2={PAD_TOP + plotH}
            stroke="var(--border)"
          />
          <path
            d={capacityPath}
            fill="none"
            stroke="var(--foreground)"
            strokeOpacity={0.7}
            strokeWidth={2}
          />
        </svg>
        {/* Hit targets: one button per month column, bigger than the marks and keyboard-reachable. */}
        <div className="absolute inset-0 flex">
          {totals.map((t, i) => (
            <button
              key={t.month}
              type="button"
              aria-label={`${formatMonthColumn(t.month, true)}: Guidance ${hrs(t.guidanceHours)}, Capacity ${hrs(t.capacityHours)}`}
              aria-pressed={selectedMonth === t.month}
              className={cn(
                "h-full flex-1 focus-visible:outline-2 focus-visible:outline-ring",
                !onSelectMonth && "cursor-default",
              )}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(i)}
              onBlur={() => setHover(null)}
              onClick={() => onSelectMonth?.(t.month)}
            />
          ))}
        </div>
        {hovered && hover !== null && (
          <div
            className="pointer-events-none absolute top-0 z-10 w-56 rounded-md border border-border bg-popover p-2.5 text-xs shadow-md"
            style={{
              left: `${Math.min(((hover + 0.5) / totals.length) * 100, 75)}%`,
              transform: "translateX(-50%)",
            }}
          >
            <div className="mb-1 font-semibold text-foreground">
              {formatMonthColumn(hovered.month, true)}
            </div>
            <div className="mb-1.5 text-muted-foreground">{BASIS_LABEL[hovered.basis]}</div>
            <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 tabular-nums">
              <dt className="text-muted-foreground">Capacity</dt>
              <dd className="text-right">{hrs(hovered.capacityHours)}</dd>
              <dt className="text-muted-foreground">Guidance</dt>
              <dd className="text-right">{hrs(hovered.guidanceHours)}</dd>
              {hovered.basis !== "forecast" && (
                <>
                  <dt className="text-muted-foreground">Actuals</dt>
                  <dd className="text-right">{hrs(hovered.actualHours)}</dd>
                  <dt className="text-muted-foreground">
                    Guidance %{hovered.basis === "inProgress" ? " (MTD)" : ""}
                  </dt>
                  <dd className="text-right">{pct(hovered.guidancePct)}</dd>
                </>
              )}
              {hovered.guidanceToDateHours !== null && (
                <>
                  <dt className="text-muted-foreground">Guidance to date</dt>
                  <dd className="text-right">{hrs(hovered.guidanceToDateHours)}</dd>
                </>
              )}
              <dt className="text-muted-foreground">
                {hovered.overallocatedHours > 0 ? "Overallocated" : "Available"}
              </dt>
              <dd className="text-right">
                {hrs(
                  hovered.overallocatedHours > 0
                    ? hovered.overallocatedHours
                    : hovered.availableHours,
                )}
              </dd>
            </dl>
          </div>
        )}
      </div>
    </figure>
  );
}
